import React, { useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  Box, Button, Column, Tab, TabPanel, Tabs, Typography,
} from '@tempmee/react';
import { useGetLocationUser } from '../../../domain/Users';
import { DetailHeader } from '../../commonComponents/DetailHeader';
import NotificationSettings from '../../commonComponents/NotificationSettings';
import NPSInfo from '../../commonComponents/NPSInfo';
import { Spinner } from '../../commonComponents/Spinner';
import { AccountInfoTab } from './components/AccountInfoTab';
import { LocationsTab } from './components/LocationsTab';
import { DEFAULT_LOCATION_USER_TAB, LOCATION_USER_TABS, isLocationUserTab } from './constants';
import { locationUserPath, locationUserTabPath } from './paths';

/**
 * Users → one Location User (an auth-service identity), at
 * `/location-users/:identityId/:tab?` (GOTU-19602): its name, then four tabs.
 *
 * Each tab is a link to its own slug, as the office profile's are, so a tab can
 * be shared or reopened directly. The bare URL shows Account Info, and an
 * unknown slug is replaced with the bare URL rather than leaving no tab open.
 *
 * Notifications and NPS are the office profile's own tabs, given the identity's
 * id: notification-sender and auth-service address the same account by it.
 */
export const LocationUserProfile = () => {
  const { identityId, tab } = useParams();
  const history = useHistory();
  const { user, isPending, isError, isNotFound, refetch } = useGetLocationUser(identityId);
  const isUnknownTab = tab !== undefined && !isLocationUserTab(tab);

  useEffect(() => {
    if (isUnknownTab) history.replace(locationUserPath(identityId));
  }, [history, identityId, isUnknownTab]);

  if (isPending) {
    return (
      <Box py={6}>
        <Spinner />
      </Box>
    );
  }

  // Two different dead ends, kept distinct as PromoCodeDetail keeps them: an id
  // auth-service has no user for, and a request that failed and may succeed if
  // tried again.
  if (isError || isNotFound) {
    return (
      <Column px={8} pt={6} gap={3} align="start">
        <Typography>
          {isError ? 'Could not load this location user.' : 'Location user not found.'}
        </Typography>
        {isError ? (
          <Button variant="outlined" color="secondary" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        ) : null}
      </Column>
    );
  }

  // Keyed by the slugs in LOCATION_USER_TABS. Only the open tab's panel mounts.
  const panels = {
    account: <AccountInfoTab user={user} />,
    locations: <LocationsTab />,
    notifications: <NotificationSettings userId={identityId} />,
    nps: <NPSInfo userId={identityId} />,
  };

  return (
    <Column pb={12}>
      <DetailHeader title={user.name} />

      <Column px={8} pt={4}>
        <Tabs value={isLocationUserTab(tab) ? tab : DEFAULT_LOCATION_USER_TAB}>
          {LOCATION_USER_TABS.map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              component={Link}
              to={locationUserTabPath(identityId, value)}
            />
          ))}

          {LOCATION_USER_TABS.map(({ value }) => (
            <TabPanel key={value} value={value}>
              {panels[value]}
            </TabPanel>
          ))}
        </Tabs>
      </Column>
    </Column>
  );
};

export default LocationUserProfile;
